// ─────────────────────────────────────────────────────────────────────────────
// Google Ads click attribution (GCLID)
//
// Visitors arriving from a Google Ads click land with a ?gclid=... URL
// parameter (auto-tagging). We stash it in sessionStorage so the contact form
// can attach it to its submission; contact.php then forwards it for offline
// conversion import into Google Ads (see scripts/google-ads-conversion-webhook.gs).
//
// Privacy: first-party, session-scoped storage of an ID Google itself placed
// in the URL. No Google scripts or cookies are involved, and the value only
// leaves the browser if the visitor submits the contact form. Legal basis:
// legitimate interest (Art. 6(1)(f) GDPR) - disclosed on the Privacy & GDPR
// page. Independent of the analytics consent flow in analytics.ts.
// ─────────────────────────────────────────────────────────────────────────────

const GCLID_KEY = "gclid";

// Google Ads only matches offline conversions to clicks up to 90 days old.
const GCLID_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

// gclids are URL-safe tokens; discard anything that doesn't look like one.
const GCLID_PATTERN = /^[\w-]{1,255}$/;

interface StoredGclid {
  value: string;
  ts: number;
}

/** Persist the gclid from a landing URL, if present. Safe to call anywhere. */
export const captureGclid = (search: string): void => {
  if (typeof window === "undefined") return;
  try {
    const gclid = new URLSearchParams(search).get("gclid");
    if (!gclid || !GCLID_PATTERN.test(gclid)) return;
    const payload: StoredGclid = { value: gclid, ts: Date.now() };
    window.sessionStorage.setItem(GCLID_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable (private browsing etc.) - attribution is best-effort.
  }
};

/** The stored gclid for the current session, or null if absent/expired. */
export const getStoredGclid = (): string | null => {
  if (typeof window === "undefined") return null;

  const discard = (): null => {
    try {
      window.sessionStorage.removeItem(GCLID_KEY);
    } catch {
      // noop - storage unavailable
    }
    return null;
  };

  try {
    const raw = window.sessionStorage.getItem(GCLID_KEY);
    if (!raw) return null;
    const { value, ts } = JSON.parse(raw) as StoredGclid;
    if (!value || !GCLID_PATTERN.test(value)) return discard();
    if (typeof ts !== "number" || Date.now() - ts > GCLID_MAX_AGE_MS) return discard();
    return value;
  } catch {
    // Corrupt JSON - remove it so it isn't re-parsed on every call.
    return discard();
  }
};
